import React, { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { Alert, DialogContent, Grid, MenuItem, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { HourglassEmpty } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import {
  AccountData,
  PaymentEntityType,
  RefundablePaymentData,
  RefundDisabledReason,
  RefundResultData,
  RefundsService,
  RefundStatus,
} from '../api';
import { ApiError } from '../api/core/ApiError';
import { formatMoney } from '../common/bookingFee';
import { ColorButton } from '../common/ui/ConfimationDialog';
import { ApiResponseHandlerType } from '../common/apiNotifications/ApiResponseHandlerContext';
import { useApiResponseHandlerContext } from '../common/apiNotifications/useApiResponseHandlerContext';
import { getDateFormat } from '../common/getDateAndTimeFormat';
import { useAuth } from '../session/InternalAuthProvider';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { formatRefundCardSuffix, formatRefundDate, useRefundAmountFormatter } from './refundFormat';

type RefundFormValues = {
  amount: number | '';
  reasonId: string;
  pin: string;
};

type ProviderError = {
  message: string;
  code: string;
};

type RefundDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  item: RefundablePaymentData | undefined;
  ownerLabel: string;
  voucherBalance?: number;
  onRefunded: () => void;
};

const AMOUNT_SX = { fontVariantNumeric: 'tabular-nums' };

export const RefundDialog = ({ isOpen, onClose, item, ownerLabel, voucherBalance, onRefunded }: RefundDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const posthog = usePostHog();
  const { showResponseNotification } = useApiResponseHandlerContext();
  const { authState } = useAuth();
  const authAccount = authState.account as unknown as AccountData;
  const { account } = useGetAccountAuthenticated({});
  const formatAmount = useRefundAmountFormatter();
  const [providerError, setProviderError] = useState<ProviderError | undefined>(undefined);
  const [pendingResult, setPendingResult] = useState<RefundResultData | undefined>(undefined);

  if (!item) {
    return null;
  }

  const refundReasons = account?.refund_reasons ?? [];
  const isPinRequired = !!account?.refund_pin_required;
  const isVoucher = item.entity_type === PaymentEntityType.VOUCHER;
  const refundableAmount = item.refundable_amount ?? 0;
  const { longDateFormat } = getDateFormat(authAccount);
  const itemLabel = isVoucher ? ownerLabel : item.label ?? t(`refunds.entity.${item.entity_type}`);

  const validationSchema = yup.object().shape({
    amount: yup
      .number()
      .typeError(t('calendar.edit_order.mandatoryField'))
      .min(0.01, t('calendar.edit_order.positive'))
      .max(refundableAmount, t('refunds.dialog.amountTooHigh', { max: formatAmount(refundableAmount) }))
      .required(t('calendar.edit_order.mandatoryField')),
    reasonId: yup.string().required(t('calendar.edit_order.mandatoryField')),
    pin: isPinRequired ? yup.string().required(t('calendar.edit_order.mandatoryField')) : yup.string(),
  });

  const initialValues: RefundFormValues = {
    amount: refundableAmount,
    reasonId: refundReasons[0]?.id ?? '',
    pin: '',
  };

  const handleClose = () => {
    setProviderError(undefined);
    setPendingResult(undefined);
    onClose();
  };

  const mapErrorMessage = (error: ApiError): string => {
    const detail = error.body?.detail;
    if (Object.values(RefundDisabledReason).includes(detail)) {
      return t(`refunds.disabled.${detail}`);
    }
    if (typeof detail === 'string') {
      return detail;
    }
    return t('api.error.default');
  };

  const onSubmit = async (values: RefundFormValues, { setFieldError }: FormikHelpers<RefundFormValues>) => {
    setProviderError(undefined);
    try {
      const result = await RefundsService.createRefund({
        charge_id: item.charge_id as string,
        amount: Number(values.amount),
        reason_id: values.reasonId,
        pin: isPinRequired ? values.pin : undefined,
      });
      posthog.capture('Refund Created', {
        status: result.status,
        amount: Number(values.amount),
        entity_type: item.entity_type,
      });
      onRefunded();
      if (result.status === RefundStatus.PENDING) {
        setPendingResult(result);
        return;
      }
      showResponseNotification(t('refunds.dialog.success'), ApiResponseHandlerType.SUCCESS);
      handleClose();
    } catch (error) {
      if (!(error instanceof ApiError)) {
        console.error('An unexpected api error occurred:', error);
        showResponseNotification('An unexpected error occurred', ApiResponseHandlerType.ERROR);
        return;
      }
      const detail = error.body?.detail;
      if (detail === 'wrong refund pin') {
        setFieldError('pin', t('refunds.dialog.pinWrong'));
      } else if (detail?.provider_error ?? detail?.grow_error) {
        const providerError = detail.provider_error ?? detail.grow_error;
        setProviderError({
          message: providerError.message ?? '',
          code: String(providerError.id ?? ''),
        });
      } else {
        showResponseNotification(mapErrorMessage(error), ApiResponseHandlerType.ERROR);
      }
    }
  };

  const summaryRow = (label: string, amount: number, isBold = false, format = formatAmount) => (
    <Grid container justifyContent="space-between" flexWrap="nowrap">
      <Typography variant="body2" fontWeight={isBold ? 600 : undefined}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={isBold ? 600 : undefined} dir="ltr" sx={AMOUNT_SX}>
        {format(amount)}
      </Typography>
    </Grid>
  );

  if (pendingResult) {
    const { refund } = pendingResult;
    return (
      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{t('refunds.pending.title')}</DialogTitle>
        <DialogContent>
          <Grid container flexDirection="column" flexWrap="nowrap" gap={2}>
            <Alert severity="warning">{t('refunds.pending.body')}</Alert>
            <Grid
              container
              flexDirection="column"
              gap={0.75}
              borderRadius="8px"
              border="1px solid"
              borderColor="divider"
              p={1.5}>
              <Grid container justifyContent="space-between" flexWrap="nowrap">
                <Typography variant="body2" fontWeight={600}>
                  {refund.label ?? itemLabel}
                </Typography>
                <Typography variant="body2" fontWeight={600} dir="ltr" sx={AMOUNT_SX}>
                  {formatAmount(refund.amount)}
                </Typography>
              </Grid>
              <Typography variant="caption" color="text.secondary">
                {[formatRefundCardSuffix(refund.card_suffix), refund.reason_label].filter(Boolean).join(' · ')}
              </Typography>
              <Grid container alignItems="center" gap={0.5}>
                <HourglassEmpty sx={{ fontSize: 16, color: 'secondary.dark' }} />
                <Typography variant="caption" fontWeight={500} color="secondary.dark">
                  {t('refunds.section.statusPending')}
                </Typography>
              </Grid>
            </Grid>
            <Typography variant="body2">{t('refunds.pending.next')}</Typography>
          </Grid>
        </DialogContent>
        <DialogActions>
          <LoadingButton variant="contained" onClick={handleClose} sx={{ boxShadow: 'none' }} autoFocus>
            {t('refunds.pending.gotIt')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ values, errors, touched, handleChange, setFieldValue, submitForm, isSubmitting }) => (
        <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs" fullScreen={isFullScreen}>
          <DialogTitle>{t('refunds.dialog.title')}</DialogTitle>
          <DialogContent>
            <Grid container flexDirection="column" flexWrap="nowrap" gap={2}>
              {providerError && (
                <Alert severity="error">
                  {t('refunds.dialog.providerError', { message: providerError.message, code: providerError.code })}
                </Alert>
              )}
              <Grid
                container
                flexDirection="column"
                flexWrap="nowrap"
                gap={0.75}
                borderRadius="8px"
                border="1px solid"
                borderColor="divider"
                p={1.5}>
                <Typography fontWeight={700}>{itemLabel}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('refunds.dialog.cardPaidOn', {
                    suffix: formatRefundCardSuffix(item.card_suffix),
                    date: formatRefundDate(item.paid_at, longDateFormat),
                  })}
                  {!isVoucher && ownerLabel ? ` · ${ownerLabel}` : ''}
                </Typography>
                {!providerError && summaryRow(t('refunds.dialog.paid'), item.amount ?? 0)}
                {!providerError &&
                  (item.booking_fee_amount ?? 0) > 0 &&
                  summaryRow(
                    t('refunds.dialog.bookingFeeNonRefundable'),
                    -(item.booking_fee_amount ?? 0),
                    false,
                    (amount) => formatMoney(amount, authAccount),
                  )}
                {!providerError &&
                  (isVoucher && voucherBalance !== undefined
                    ? summaryRow(t('refunds.dialog.voucherBalance'), voucherBalance)
                    : summaryRow(t('refunds.dialog.refundedSoFar'), item.refunded_amount ?? 0))}
                {summaryRow(t('refunds.dialog.available'), refundableAmount, true)}
              </Grid>
              <TextField
                size="small"
                type="number"
                name="amount"
                label={t('refunds.dialog.amount')}
                value={values.amount}
                onChange={handleChange}
                inputProps={{ inputMode: 'decimal', step: 'any', dir: 'ltr' }}
                error={Boolean(errors.amount)}
                helperText={
                  errors.amount ||
                  (isVoucher
                    ? t('refunds.dialog.voucherHelper')
                    : t('refunds.dialog.amountHelper', { max: formatAmount(refundableAmount) }))
                }
              />
              <TextField
                select
                size="small"
                name="reasonId"
                label={t('refunds.dialog.reason')}
                value={values.reasonId}
                onChange={(event) => setFieldValue('reasonId', event.target.value)}
                error={touched.reasonId && Boolean(errors.reasonId)}
                helperText={touched.reasonId && errors.reasonId}>
                {refundReasons.map((reason) => (
                  <MenuItem key={reason.id} value={reason.id}>
                    {reason.label}
                  </MenuItem>
                ))}
              </TextField>
              {isPinRequired && (
                <TextField
                  size="small"
                  type="password"
                  name="pin"
                  label={t('refunds.dialog.pin')}
                  value={values.pin}
                  onChange={handleChange}
                  inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                  error={Boolean(errors.pin)}
                  helperText={errors.pin}
                />
              )}
              {isVoucher && Number(values.amount) > 0 && voucherBalance !== undefined && !providerError && (
                <Alert severity="warning">
                  {t('refunds.dialog.voucherWarning', {
                    amount: formatAmount(Number(values.amount)),
                    balance: formatAmount(Math.max(voucherBalance - Number(values.amount), 0)),
                  })}
                </Alert>
              )}
              {!providerError && <Alert severity="info">{t('refunds.dialog.notice')}</Alert>}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
            <ColorButton onClick={handleClose}>{providerError ? t('refunds.dialog.close') : t('cancel')}</ColorButton>
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              onClick={submitForm}
              sx={{ boxShadow: 'none' }}
              autoFocus>
              {providerError
                ? t('refunds.dialog.retry')
                : t('refunds.dialog.submit', { amount: formatAmount(Number(values.amount) || 0) })}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
};
